import {PersonType} from "../../../../types/person-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAt,
    faMessage,
    faPhone,
    faUpRightFromSquare, IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import AppLink from "../../../common/AppLink";
import {AppRoutes} from "config/router";
import If from "components/common/If";
import useAppContext from "../../../../utilities/hooks/useAppContext";
import CopyButton from "components/common/button/CopyButton";
import { uuid } from "config/app-constants";

type PersonPropertyViewModel = {
    personName: string,
    personId: number,
    contactInfo?: {
        name: string
        value: any,
        link?: string | null,
        id: number | string
    }[],
    tags?: PersonType['tags'],
    countryCode?: string | null,
    country?: {
        name: string,
        flag: string,
        alpha3Code: string,
        translate?: string | null
    } | null
}

type PersonPropertyViewProps = PersonPropertyViewModel & {
    name: string
    copy?: boolean,
    open?: boolean
}

type PersonPropertyContactLinkProps = {
    info: NonNullable<PersonPropertyViewProps['contactInfo']>[0],
    copy?: boolean,
    open?: boolean,
    name: string
}

type PersonFromGraphQLQuery = {
    id: number,
    name: string,
    contactInfo?: {
        items: {
            value?: string,
            link?: string | null,
            name: string,
            id?: number,
        }[]
    },
    tags: {
        items: {
            tag: {
                name: string
            }
        }[]
    },
    countryCode?: string | null,
    country?: {
        name: string,
        flag: string,
        alpha3Code: string
    } | null
}
export function resolvePersonFromGraphQLQuery(model: PersonFromGraphQLQuery): PersonPropertyViewModel {
    return {
        personName: model.name,
        personId: model.id,
        contactInfo: model.contactInfo?.items.map(i => ({
            name: i.name,
            value: i.value,
            link: i.link,
            id: uuid()
        })),
        tags: model.tags.items.map(i => ({
            id: uuid(),
            tagId: uuid(),
            name: i.tag.name
        })),
        countryCode: model.countryCode,
        country: model.country
    }
}

const PersonPropertyView = (props: PersonPropertyViewProps) => {
    if (!props.personName) return <></>;

    return <div>
        <div>
            <If condition={props.open}>
                <AppLink title="button.open" to={AppRoutes.PERSON_VIEW} params={{id: props.personId}} className="hover:underline">
                    <span className="pr-4">{props.personName}</span>
                    <FontAwesomeIcon icon={faUpRightFromSquare}/>
                </AppLink>
            </If>
            <If condition={!props.open}>
                <>
                    <span>{props.personName}</span>
                </>
            </If>
            <If condition={props.copy}>
                <CopyButton text={props.personName} />
            </If>
        </div>
        <If condition={props.country} >
            <div className="flex gap-1 w-max">
                <img src={props.country?.flag} className="max-h-4 my-auto" alt={props.country?.alpha3Code} />
                <span>{props.country?.translate || props.country?.name}</span>
                <span>({props.country?.alpha3Code})</span>
            </div>
        </If>
        {props.contactInfo?.map((item, index) => {
            return <div key={item.id}>
                <PersonPropertyContactLink
                    info={item}
                    open={props.open}
                    copy={props.copy}
                    name={props.name + "-contact-" + item.id}
                />
            </div>;
        }) || <></>}
    </div>
};

const PersonPropertyContactLink = (props: PersonPropertyContactLinkProps) => {
    const context = useAppContext();
    let config: {
        icon: IconDefinition,
        href: string,
        title: string,
        openExternal: {
            icon: IconDefinition,
            href: string,
            title: string,
            name: string
        }[]
    };

    switch (props.info.name) {
        case 'tel':
            config = {
                icon: faPhone,
                href: 'tel:' + props.info.value,
                title: context.t('common.call'),
                openExternal: [
                    {
                        href: 'sms:' + props.info.value,
                        icon: faMessage,
                        name: 'sms',
                        title: context.t('common.sendSMS')
                    }
                ]
            };
            break;
        default:
            config = {
                icon: faAt,
                href: 'mailto:' + + props.info.value,
                title: context.t('common.sendMail'),
                openExternal: []
            };
            break;
    }

    return <div>
        <If condition={props.open}>
            <a href={config.href} title={config.title} className="hover:underline">
                <span className="text-sm">
                    <FontAwesomeIcon icon={config!.icon}/>
                </span>
                <span className="p-2">{props.info.value}</span>
            </a>
        </If>
        <If condition={!props.open}>
            <span className="text-sm">
                <FontAwesomeIcon icon={config!.icon}/>
            </span>
            <span className="p-2">{props.info.value}</span>
        </If>
        {config!.openExternal.map(e => (
            <If condition={props.open} key={e.name + props.name}>
                <a href={e.href} title={e.title} className="p-2">
                    <FontAwesomeIcon icon={e.icon}/>
                </a>
            </If>
        ))}
        <If condition={props.copy}>
            <CopyButton text={props.info.value} />
        </If>
    </div>
}

export default PersonPropertyView;
